import { useRef, useState } from 'react';
import { z } from 'zod';
import type {
  BudgetPost,
  CreateInvoiceCommand,
  InvoiceDto,
  PhaseId,
  UpdateInvoiceCommand,
} from '@/api/generated';
import {
  useCreateInvoice,
  useDocuments,
  useInvoices,
  useUpdateInvoice,
  useUploadDocument,
} from '@/api/hooks';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import { BUDGET_POSTS as DOMAIN_BUDGET_POSTS } from '@/domain/budgetPosts';
import styles from './InvoiceDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const schema = z.object({
  supplier: z.string().trim().min(2, 'Minstens 2 tekens'),
  amount: z
    .string()
    .min(1, 'Verplicht veld')
    .refine((v) => /^\d+([.,]\d{1,2})?$/.test(v.trim()), 'Bedrag in euro (bv. 18500 of 1200,50)')
    .refine((v) => Number(v.trim().replace(',', '.')) > 0, 'Bedrag moet positief zijn'),
  dueAt: z.string().min(1, 'Kies een vervaldatum'),
  phaseId: z.string().min(1, 'Kies een fase'),
  // BF-13 — empty string means "no post yet", BudgetPost otherwise.
  budgetPost: z.string(),
  // 2026-05-17 — empty string means "no doc linked".
  documentId: z.string(),
});

type InvoiceFormValues = z.infer<typeof schema>;

function FieldsBody({
  currentDocumentId,
}: {
  currentDocumentId?: string;
}) {
  // 2026-05-17 — picker is scoped to docs tagged `factuur` and excludes any
  // document that's already linked to another invoice. The currently-selected
  // doc (edit dialog) is always kept available so the field reflects state.
  const docs = useDocuments({ pageSize: 100, tag: 'factuur' });
  const linkedQuery = useInvoices({ pageSize: 500 });
  const linkedIds = new Set(
    (linkedQuery.data?.items ?? [])
      .map((i) => i.documentId)
      .filter((id): id is string => typeof id === 'string'),
  );
  // Remove the current invoice's own link so its file stays selectable.
  if (currentDocumentId) linkedIds.delete(currentDocumentId);
  const allDocs = docs.data?.items ?? [];
  const docOptions = allDocs.filter((d) => !linkedIds.has(d.id));

  const upload = useUploadDocument();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onPickFile = () => fileRef.current?.click();

  return (
    <>
      <FormField<InvoiceFormValues> name="supplier" label="Leverancier" required>
        {(field) => <Input {...field} autoFocus placeholder="Bv. Heyvaert Bouw" />}
      </FormField>
      <FormField<InvoiceFormValues> name="amount" label="Bedrag (€, incl. BTW)" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="text"
            inputMode="decimal"
            placeholder="18500"
          />
        )}
      </FormField>
      <FormField<InvoiceFormValues> name="dueAt" label="Vervaldatum" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="date"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      </FormField>
      <FormField<InvoiceFormValues> name="phaseId" label="Fase" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
      <FormField<InvoiceFormValues> name="budgetPost" label="Budgetpost">
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            <option value="">— niet geboekt —</option>
            {DOMAIN_BUDGET_POSTS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
      <FormField<InvoiceFormValues> name="documentId" label="Gekoppeld bestand">
        {(field) => (
          <div className="col gap-6">
            <div className="row gap-6">
              <select
                {...field}
                className={`flex-1 ${styles.select}`}
                value={(field.value as string) ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                disabled={upload.isPending}
              >
                <option value="">— geen bestand —</option>
                {docOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
                {/* Keep the currently-linked doc in the list even if it's not
                    in docOptions (e.g. it lost the factuur tag). */}
                {currentDocumentId && !docOptions.some((d) => d.id === currentDocumentId) && (
                  <option value={currentDocumentId}>
                    {allDocs.find((d) => d.id === currentDocumentId)?.name ??
                      '(gekoppeld bestand)'}
                  </option>
                )}
              </select>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/*"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setUploadError(null);
                  try {
                    const result = await upload.mutateAsync({
                      file: f,
                      tags: ['factuur'],
                    });
                    if (result && typeof result === 'object' && 'id' in result) {
                      field.onChange(String((result as { id: string }).id));
                    }
                  } catch (err) {
                    setUploadError(
                      err instanceof Error ? err.message : 'Upload mislukt.',
                    );
                  } finally {
                    e.target.value = '';
                  }
                }}
              />
              <Btn type="button" onClick={onPickFile} disabled={upload.isPending}>
                <Icon name="upload" size={12} />
                {upload.isPending ? 'Bezig…' : 'Upload'}
              </Btn>
            </div>
            {uploadError && (
              <span className={styles.fieldErr} role="alert">
                {uploadError}
              </span>
            )}
            <span className={styles.fieldHint}>
              Toont alleen documenten met tag <code>factuur</code>. Upload tagt automatisch.
            </span>
          </div>
        )}
      </FormField>
    </>
  );
}

// Coerce form's "" string back to a null BudgetPost so the wire contract is
// preserved (the backend rejects an unknown enum string).
function asBudgetPost(value: string): BudgetPost | null {
  if (!value) return null;
  return value as BudgetPost;
}

export function InvoiceCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateInvoice();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuwe factuur</DialogTitle>
        <DialogDescription>
          Voeg een factuur toe. Status start op "Open" tot je ze goedkeurt.
        </DialogDescription>
        <DialogBody>
          <Form<InvoiceFormValues>
            schema={schema}
            defaultValues={{
              supplier: '',
              amount: '',
              dueAt: '',
              phaseId: defaultPhaseId ?? 'Ruw',
              budgetPost: '',
              documentId: '',
            }}
            onSubmit={async (values) => {
              const body: CreateInvoiceCommand = {
                supplier: values.supplier.trim(),
                amount: values.amount.trim().replace(',', '.'),
                dueAt: values.dueAt,
                phaseId: values.phaseId,
                budgetPost: asBudgetPost(values.budgetPost),
                documentId: values.documentId || null,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody currentDocumentId={undefined} />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceEditDialog({
  open,
  onOpenChange,
  invoice,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: InvoiceDto;
}) {
  const updateMutation = useUpdateInvoice(invoice.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Factuur bewerken</DialogTitle>
        <DialogDescription>Pas leverancier, bedrag, vervaldatum of fase aan.</DialogDescription>
        <DialogBody>
          <Form<InvoiceFormValues>
            schema={schema}
            defaultValues={{
              supplier: invoice.supplier,
              amount: String(invoice.amount ?? ''),
              dueAt: (invoice.dueAt ?? '').slice(0, 10),
              phaseId: invoice.phaseId ?? 'Ruw',
              budgetPost: invoice.budgetPost ?? '',
              documentId: invoice.documentId ?? '',
            }}
            onSubmit={async (values) => {
              const body: UpdateInvoiceCommand = {
                supplier: values.supplier.trim() || null,
                amount: values.amount.trim().replace(',', '.'),
                dueAt: values.dueAt || null,
                phaseId: values.phaseId,
                budgetPost: asBudgetPost(values.budgetPost),
                documentId: values.documentId || null,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody currentDocumentId={invoice.documentId ?? undefined} />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
